import * as Sentry from "@sentry/browser";
import { config } from "~/js/network/network-handler";

/**
 * Initialize Sentry for error tracking and performance monitoring
 */
export const initSentry = () => {
	// Skip initialization on localhost
	if (window.location.hostname === "localhost") {
		console.log("[Sentry] Skipped on localhost");
		return;
	}

	// Get DSN from config or environment
	const dsn = import.meta.env.FRONT_VITE_SENTRY_DSN;

	if (!dsn) {
		console.warn("[Sentry] DSN not configured, skipping initialization");
		return;
	}

	Sentry.init({
		dsn,
		environment: config?.ENV || "unknown",
		// Release name should match the one uploaded with source maps
		release: `phygital-plus-web-editor@${config?.PROJECT_VERSION || "unknown"}`,

		// Performance Monitoring
		tracesSampleRate: 0.1, // 10% of transactions

		// Session Replay
		replaysSessionSampleRate: 0.1, // 10% of sessions
		replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

		// Integrations
		integrations: [
			Sentry.browserTracingIntegration(),
			Sentry.replayIntegration({
				maskAllText: false,
				blockAllMedia: false
			})
		],

		// Error filtering
		beforeSend(event, hint) {
			// Filter out network errors from service worker
			if (hint.originalException && typeof hint.originalException === "object") {
				const error = hint.originalException as Error;
				if (error.message?.includes("no-response")) {
					return null; // Don't send to Sentry
				}
			}

			// Add user context if available
			const userId = localStorage.getItem("user_id");
			if (userId) {
				event.user = { id: userId };
			}

			return event;
		},

		// Ignore specific errors
		ignoreErrors: [
			// Browser extensions
			"top.GLOBALS",
			"chrome-extension://",
			"moz-extension://",
			// Random network errors
			"NetworkError",
			"Failed to fetch",
			// Service Worker errors
			"no-response"
		]
	});

	// Set user context if available
	const userId = localStorage.getItem("user_id");
	if (userId) {
		Sentry.setUser({ id: userId });
	}

	console.log("[Sentry] Initialized successfully");
};

/**
 * Capture exception manually
 */
export const captureException = (error: Error, context?: Record<string, unknown>) => {
	if (context) {
		Sentry.captureException(error, { extra: context });
	} else {
		Sentry.captureException(error);
	}
};

/**
 * Capture message manually
 */
export const captureMessage = (message: string, level: Sentry.SeverityLevel = "info") => {
	Sentry.captureMessage(message, level);
};

/**
 * Add breadcrumb for debugging
 */
export const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb) => {
	Sentry.addBreadcrumb(breadcrumb);
};

/**
 * Set user context
 */
export const setUser = (user: { id: string; email?: string; username?: string }) => {
	Sentry.setUser(user);
};
